import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { readJSON } from '../storage';
import { STORAGE_KEYS } from '../constants';
import { refreshAccessToken } from '../api';

/**
 * The app's socket connection — one, shared.
 *
 * This used to call `io()` inside the hook's own effect, so it was not a
 * singleton: every component that wanted realtime opened its own websocket. Six
 * call sites meant six connections per signed-in user, the server fanning every
 * broadcast out six times to one browser, and presence disagreeing between panes
 * because each pane was a distinct client to the server.
 *
 * The connection now lives at module scope, keyed on the access token. Consumers
 * subscribe and are notified when it is created or replaced, so the hook's
 * contract is unchanged: it returns the socket, or null until one exists.
 *
 * IMPORTANT for consumers: because the socket is shared, `socket.off('event')`
 * removes EVERY listener for that event, including other components'. Always
 * pass the handler — `socket.off('event', handler)`.
 */

let sharedSocket = null;
let connectedWith = null;
const subscribers = new Set();

const publish = () => subscribers.forEach((notify) => notify(sharedSocket));

/**
 * How many times one connection may rotate its token and retry the handshake
 * before giving up. Two covers the real case (the persisted access token had
 * expired) without turning a genuinely revoked session into a retry loop
 * against the auth service.
 */
const MAX_AUTH_RETRIES = 2;

/**
 * Recover from a REFUSED handshake.
 *
 * Access tokens live ten minutes (auth's JWT_ACCESS_EXPIRY) and the handshake
 * carries whatever is in storage, so any app load that happens more than ten
 * minutes after the last token rotation connects with an expired token and the
 * server's socketAuth middleware rejects it.
 *
 * That rejection used to be terminal and completely silent. Socket.IO does not
 * reconnect after a middleware error — it parks the client with `active: false`
 * — and `emit()` on a disconnected socket neither throws nor reports: it queues
 * the packet in an internal buffer that is flushed on a connect that never
 * comes. Meanwhile the HTTP layer refreshed its token on the first 401 and
 * carried on, so the app looked healthy while every realtime write (sending a
 * message, typing, read receipts) was swallowed. The only cure was a reload,
 * because a reload rebuilt the socket with the token HTTP had since refreshed.
 *
 * So: rotate through the same single-flight refresh the HTTP layer uses, then
 * reconnect. The `auth` callback re-reads storage on every attempt, which is
 * what makes reconnecting enough — the socket does not have to be rebuilt.
 */
let authRetries = 0;

const recoverAuth = async (socket) => {
  if (socket !== sharedSocket) return;
  // Connected, or Socket.IO is already retrying on its own backoff — either way
  // there is nothing parked to revive, and reconnecting by hand would fight it.
  if (socket.connected || socket.active) return;
  if (authRetries >= MAX_AUTH_RETRIES) return;
  authRetries += 1;

  const fresh = await refreshAccessToken();
  // No fresh token means the refresh token is gone too. Leave it: the next HTTP
  // 401 hard-logs-out, and reconnecting here would race that redirect.
  if (!fresh) return;
  // A token rotation, a sign-out, or a competing revive may have moved things on
  // while the refresh was in flight.
  if (socket !== sharedSocket || socket.connected || socket.active) return;

  socket.connect();
};

const attachAuthRecovery = (socket) => {
  socket.on('connect', () => {
    authRetries = 0;
  });

  socket.on('connect_error', () => {
    // `active` still true means a transport-level failure (server down, network
    // drop) and the built-in reconnection is handling it. Only a handshake
    // rejection clears the flag, and that is the case to recover from.
    if (socket.active) return;
    recoverAuth(socket);
  });
};

/**
 * Second chance for a socket that has already exhausted its retry budget.
 *
 * The budget exists so a revoked session cannot spin against the auth service,
 * but it also means a connection parked early in a long-lived tab stays parked.
 * Coming back to the tab or regaining the network is a fresh, user-driven
 * opportunity rather than a continuation of the failed attempt, so the count is
 * released — bounded by the fact that a doomed refresh fails immediately.
 */
const reviveOnUserSignal = () => {
  if (!sharedSocket || sharedSocket.connected || sharedSocket.active) return;
  authRetries = 0;
  recoverAuth(sharedSocket);
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', reviveOnUserSignal);
}
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reviveOnUserSignal();
  });
}

const openSocket = (token) => {
  // Same token, live socket — reuse it. This is what makes the hook shared
  // rather than per-component.
  if (sharedSocket && connectedWith === token) return sharedSocket;

  // Token rotated: drop the old connection so the new one authenticates fresh.
  if (sharedSocket) sharedSocket.disconnect();

  connectedWith = token;
  authRetries = 0;
  sharedSocket = io(process.env.REACT_APP_SOCKET_URL, {
    // Function form so every (re)connect reads the freshest access token from
    // storage — central EdDSA tokens are short-lived and the HTTP layer rotates
    // them on 401, so a reconnect must not reuse a stale closure token.
    auth: (cb) => cb({ token: readJSON(STORAGE_KEYS.AUTH_TOKEN) || token }),
  });

  attachAuthRecovery(sharedSocket);
  publish();
  return sharedSocket;
};

const closeSocket = () => {
  if (!sharedSocket) return;
  sharedSocket.disconnect();
  sharedSocket = null;
  connectedWith = null;
  // The budget belongs to a connection, not to the session — a later sign-in
  // must not inherit the previous one's exhausted count.
  authRetries = 0;
  publish();
};

/**
 * Is it worth handing this socket an `emit`?
 *
 * Connected is obviously fine. `active` — connecting, or reconnecting on the
 * built-in backoff — is fine too: Socket.IO buffers the packet and flushes it on
 * connect, acknowledgements included, which is the offline queue working as
 * intended. Neither one means the connection is PARKED: rejected at the
 * handshake, with nothing scheduled to try again. An emit into that goes into a
 * buffer that is never flushed, and Socket.IO neither throws nor reports it — so
 * call sites have to check before sending anything the user believes was sent.
 */
export const isRealtimeUsable = (socket) => Boolean(socket && (socket.connected || socket.active));

/** Shown when `isRealtimeUsable` is false, so the copy stays consistent. */
export const SOCKET_DOWN_MESSAGE = 'Not connected — reconnecting. Please try again in a moment.';

const SocketAPI = () => {
  const userData = useSelector((state) => state?.AppReducer?.user?.data);
  // Prefer the Redux-sourced token; fall back to what the login saga persisted.
  // `readJSON` rather than a bare parse — a malformed entry used to throw inside
  // the effect that opens the connection, which killed realtime for the whole
  // session while HTTP kept working, so the app looked online but silent.
  const token = userData?.auth_token || readJSON(STORAGE_KEYS.AUTH_TOKEN);

  const [socket, setSocket] = useState(sharedSocket);

  useEffect(() => {
    subscribers.add(setSocket);

    if (token) {
      setSocket(openSocket(token));
    } else {
      // Signed out: tear the connection down for everyone.
      closeSocket();
    }

    // Only this consumer's subscription is released here. The connection itself
    // outlives individual components — it is scoped to the session, not to a
    // pane — so unmounting one view no longer disconnects the others.
    return () => {
      subscribers.delete(setSocket);
    };
  }, [token]);

  return socket;
};

export default SocketAPI;
